import {
	Briefcase,
	Building2,
	CalendarClock,
	ChevronLeft,
	ChevronRight,
	Clock,
	Edit3,
	Send,
	Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useCompanyActivities } from "@/app/services/create-resume/queries.js";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { STORAGE_KEYS } from "./constants";
import { ActivityCard, ContentSection, FilterBar } from "./shared";

const ITEMS_PER_PAGE = 10;

const ACTIVITY_TYPE_CONFIG = {
	COMPANY_UPDATED: {
		icon: Building2,
		bgColor: "bg-teal-50",
		iconColor: "text-teal-700",
		label: "Company Updated",
	},
	JOB_CREATED: {
		icon: Briefcase,
		bgColor: "bg-[hsl(214,100%,97%)]",
		iconColor: "text-[hsl(221,83%,53%)]",
		label: "Job Created",
	},
	JOB_UPDATED: {
		icon: Edit3,
		bgColor: "bg-amber-50",
		iconColor: "text-amber-700",
		label: "Job Updated",
	},
	JOB_POSTED: {
		icon: Send,
		bgColor: "bg-emerald-50",
		iconColor: "text-emerald-700",
		label: "Job Posted",
	},
	JOB_DELETED: {
		icon: Trash2,
		bgColor: "bg-rose-50",
		iconColor: "text-rose-700",
		label: "Job Deleted",
	},
	JOB_DEADLINE_EXTENDED: {
		icon: CalendarClock,
		bgColor: "bg-blue-50",
		iconColor: "text-blue-700",
		label: "Deadline Extended",
	},
	JOB_FILLED: {
		icon: Clock,
		bgColor: "bg-gray-100",
		iconColor: "text-gray-700",
		label: "Job Filled",
	},
};

const DEFAULT_ACTIVITY_CONFIG = {
	icon: Clock,
	bgColor: "bg-gray-100",
	iconColor: "text-gray-700",
	label: "Activity",
};

const EVENT_FILTER_OPTIONS = [
	{ value: "all", label: "All Events" },
	{ value: "COMPANY_UPDATED", label: "Company Updated" },
	{ value: "JOB_CREATED", label: "Job Created" },
	{ value: "JOB_UPDATED", label: "Job Updated" },
	{ value: "JOB_POSTED", label: "Job Posted" },
	{ value: "JOB_DELETED", label: "Job Deleted" },
	{ value: "JOB_DEADLINE_EXTENDED", label: "Deadline Extended" },
	{ value: "JOB_FILLED", label: "Job Filled" },
];

function formatFieldLabel(field) {
	return String(field || "")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatActorRole(role) {
	if (!role) return "Company manager";
	return formatFieldLabel(role);
}

function formatDateDisplay(dateString) {
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return "";

	const day = date.getDate();
	const month = date.toLocaleDateString("en-US", { month: "short" });
	const year = date.getFullYear();
	const suffix =
		day === 1 || day === 21 || day === 31
			? "st"
			: day === 2 || day === 22
				? "nd"
				: day === 3 || day === 23
					? "rd"
					: "th";

	return `${day}${suffix} ${month}, ${year}`;
}

function buildActivitySubtitle(activity) {
	const metadata = activity.metadata || {};
	const parts = [];

	if (Array.isArray(metadata.changedFields) && metadata.changedFields.length) {
		parts.push(`Changed: ${metadata.changedFields.map(formatFieldLabel).join(", ")}`);
	}

	if (metadata.deadlineDate) {
		const deadline = formatDateDisplay(metadata.deadlineDate);
		if (deadline) {
			parts.push(`Deadline: ${deadline}`);
		}
	}

	if (metadata.jobStatus) {
		parts.push(`Status: ${metadata.jobStatus}`);
	}

	return parts.join(" - ");
}

function normalizeActivity(activity) {
	const timestamp = new Date(activity.createdAt);
	const fallbackId = `${activity.eventType || "activity"}-${activity.entityId || "entity"}-${activity.createdAt || ""}`;

	return {
		id: activity.id || activity._id || fallbackId,
		type: activity.eventType,
		title: activity.summary || ACTIVITY_TYPE_CONFIG[activity.eventType]?.label || "Company activity recorded",
		subtitle: buildActivitySubtitle(activity),
		timestamp: Number.isNaN(timestamp.getTime()) ? new Date() : timestamp,
		actor: activity.actorEmail
			? {
					name: activity.actorEmail,
					role: formatActorRole(activity.actorRole),
				}
			: null,
	};
}

function getUniqueActors(activities) {
	const actors = new Map();
	activities.forEach((activity) => {
		if (activity.actor?.name) {
			actors.set(activity.actor.name, activity.actor.name);
		}
	});
	return Array.from(actors.values());
}

function formatRelativeTime(date) {
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays === 1) return "Yesterday";
	if (diffDays < 7) return `${diffDays}d ago`;
	return date.toLocaleDateString();
}

function groupActivitiesByDay(activities) {
	const groups = new Map();
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	activities.forEach((activity) => {
		const activityDate = new Date(activity.timestamp);
		const activityDay = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate());

		let dayKey;
		if (activityDay.getTime() === today.getTime()) {
			dayKey = "Today";
		} else if (activityDay.getTime() === yesterday.getTime()) {
			dayKey = "Yesterday";
		} else {
			const weekday = activityDay.toLocaleDateString("en-US", { weekday: "short" });
			const monthDay = activityDay.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
			dayKey = `${weekday} - ${monthDay}`;
		}

		if (!groups.has(dayKey)) {
			groups.set(dayKey, []);
		}
		groups.get(dayKey).push(activity);
	});

	groups.forEach((group) => {
		group.sort((first, second) => second.timestamp.getTime() - first.timestamp.getTime());
	});

	return groups;
}

function getDefaultDateRange(companyId) {
	const saved = localStorage.getItem(STORAGE_KEYS.buildActivityDateRangeKey(companyId));
	if (saved) {
		try {
			return JSON.parse(saved);
		} catch {
			return null;
		}
	}

	const today = new Date();
	const monthAgo = new Date(today);
	monthAgo.setMonth(monthAgo.getMonth() - 1);

	return {
		start: monthAgo.toISOString().split("T")[0],
		end: today.toISOString().split("T")[0],
	};
}

export default function ActivityTab() {
	const { companyId } = useCompanyDashboard();
	const defaultRange = useMemo(() => getDefaultDateRange(companyId), [companyId]);
	const [searchQuery, setSearchQuery] = useState("");
	const [typeFilter, setTypeFilter] = useState("all");
	const [actorFilter, setActorFilter] = useState("all");
	const [startDate, setStartDate] = useState(defaultRange?.start || "");
	const [endDate, setEndDate] = useState(defaultRange?.end || "");
	const [collapsedDays, setCollapsedDays] = useState(new Set());
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		const nextRange = getDefaultDateRange(companyId);
		setStartDate(nextRange?.start || "");
		setEndDate(nextRange?.end || "");
	}, [companyId]);

	useEffect(() => {
		localStorage.setItem(
			STORAGE_KEYS.buildActivityDateRangeKey(companyId),
			JSON.stringify({ start: startDate, end: endDate }),
		);
	}, [companyId, endDate, startDate]);

	useEffect(() => {
		setCurrentPage(1);
	}, [typeFilter, actorFilter, startDate, endDate, searchQuery]);

	const activityParams = useMemo(() => {
		const params = {
			page: currentPage,
			limit: ITEMS_PER_PAGE,
		};

		if (searchQuery.trim()) {
			params.search = searchQuery.trim();
		}

		if (typeFilter !== "all") {
			params.eventType = typeFilter;
		}

		if (actorFilter !== "all") {
			params.actor = actorFilter;
		}

		if (startDate) {
			params.from = startDate;
		}

		if (endDate) {
			params.to = endDate;
		}

		return params;
	}, [actorFilter, currentPage, endDate, searchQuery, startDate, typeFilter]);

	const activitiesQuery = useCompanyActivities(companyId, activityParams);
	const activityResponse = activitiesQuery.data?.data || {};
	const activities = useMemo(
		() => (Array.isArray(activityResponse.items) ? activityResponse.items.map(normalizeActivity) : []),
		[activityResponse.items],
	);
	const uniqueActors = useMemo(() => getUniqueActors(activities), [activities]);
	const actorOptions = useMemo(() => {
		const options = uniqueActors.map((actor) => ({ value: actor, label: actor }));
		if (actorFilter !== "all" && !uniqueActors.includes(actorFilter)) {
			options.unshift({ value: actorFilter, label: actorFilter });
		}
		return options;
	}, [actorFilter, uniqueActors]);
	const groupedActivities = useMemo(() => groupActivitiesByDay(activities), [activities]);
	const dayKeys = Array.from(groupedActivities.keys());
	const pagination = activityResponse.pagination || {};
	const totalItems = pagination.total || 0;
	const totalPages = pagination.totalPages || 1;
	const startIndex = totalItems ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
	const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
	const hasNonDateFilters = Boolean(searchQuery.trim() || typeFilter !== "all" || actorFilter !== "all");

	function toggleDayCollapse(day) {
		setCollapsedDays((previous) => {
			const next = new Set(previous);
			if (next.has(day)) {
				next.delete(day);
			} else {
				next.add(day);
			}
			return next;
		});
	}

	return (
		<ContentSection companyHeader>
			<div className="mx-auto max-w-4xl space-y-4">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-lg font-bold text-foreground">Activity</h2>
						<p className="text-sm text-muted-foreground">
							Track recent activity on your job listings and company profile
						</p>
					</div>
				</div>

				<FilterBar
					searchValue={searchQuery}
					onSearchChange={setSearchQuery}
					searchPlaceholder="Search by job, company, or action..."
					dropdowns={[
						{
							value: typeFilter,
							onChange: setTypeFilter,
							options: EVENT_FILTER_OPTIONS,
						},
						{
							value: actorFilter,
							onChange: setActorFilter,
							options: [{ value: "all", label: "Actor" }, ...actorOptions],
						},
					]}
					dateRange={{
						startDate,
						endDate,
						onStartDateChange: setStartDate,
						onEndDateChange: setEndDate,
						showPicker: showDatePicker,
						onTogglePicker: () => setShowDatePicker((value) => !value),
						formatDateDisplay,
					}}
				/>

				<div className="relative mt-12">
					{activitiesQuery.isLoading ? (
						<div className="rounded-lg border border-border bg-white p-8 text-center">
							<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
								<Clock className="h-5 w-5 animate-pulse text-muted-foreground" />
							</div>
							<h3 className="mb-1 font-semibold text-foreground">Loading activity</h3>
							<p className="text-sm text-muted-foreground">Fetching the latest company activity.</p>
						</div>
					) : null}

					{activitiesQuery.isError ? (
						<div className="rounded-lg border border-border bg-white p-8 text-center">
							<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
								<Clock className="h-5 w-5 text-muted-foreground" />
							</div>
							<h3 className="mb-1 font-semibold text-foreground">Unable to load activities</h3>
							<p className="text-sm text-muted-foreground">Refresh the page or try again in a moment.</p>
						</div>
					) : null}

					{!activitiesQuery.isLoading && !activitiesQuery.isError
						? dayKeys.map((day, dayIndex) => {
								const dayActivities = groupedActivities.get(day) || [];
								const isCollapsed = collapsedDays.has(day);
								const isToday = day === "Today";
								const isLastDay = dayIndex === dayKeys.length - 1;

								return (
									<div key={day} className="relative">
										{!isLastDay && (
											<div
												className={`absolute left-4 top-8 w-0.5 ${
													isToday ? "border-l border-dashed border-muted-foreground/30" : "bg-border"
												}`}
												style={{ height: isCollapsed ? "24px" : "calc(100% - 8px)" }}
											/>
										)}

										<div
											className="group mb-2 flex cursor-pointer items-center justify-between"
											onClick={() => toggleDayCollapse(day)}
										>
											<div className="flex items-center gap-2">
												<div
													className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
														isToday ? "bg-primary text-white" : "bg-secondary text-muted-foreground"
													}`}
												>
													{dayActivities.length}
												</div>
												<h3 className="text-sm font-semibold text-foreground">{day}</h3>
											</div>
											<ChevronRight
												className={`h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? "" : "rotate-90"}`}
											/>
										</div>

										{!isCollapsed && (
											<div className="ml-4 space-y-2 pb-4 pl-6">
												{dayActivities.map((activity) => {
													const config = ACTIVITY_TYPE_CONFIG[activity.type] || DEFAULT_ACTIVITY_CONFIG;

													return (
														<ActivityCard
															key={activity.id}
															icon={config.icon}
															iconBgColor={config.bgColor}
															iconColor={config.iconColor}
															label={config.label}
															timestamp={formatRelativeTime(activity.timestamp)}
															title={activity.title}
															subtitle={activity.subtitle}
															actor={activity.actor}
														/>
													);
												})}
											</div>
										)}

										{isCollapsed ? <div className="h-2" /> : null}
									</div>
								);
							})
						: null}

					{!activitiesQuery.isLoading && !activitiesQuery.isError && dayKeys.length === 0 && (
						<div className="rounded-lg border border-border bg-white p-8 text-center">
							<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
								<Clock className="h-5 w-5 text-muted-foreground" />
							</div>
							<h3 className="mb-1 font-semibold text-foreground">No activities found</h3>
							<p className="text-sm text-muted-foreground">
								{hasNonDateFilters
									? "Try adjusting your filters to see more results."
									: "Activities will appear here as they happen."}
							</p>
						</div>
					)}
				</div>

				{totalItems > 0 && !activitiesQuery.isError && (
					<div className="flex items-center justify-between border-t border-border pt-4">
						<span className="text-sm text-muted-foreground">
							Showing {startIndex} - {endIndex} of {totalItems}
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
								disabled={currentPage === 1 || activitiesQuery.isFetching}
								className="rounded-lg border border-border bg-white p-1.5 transition-colors hover:bg-secondary/50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<ChevronLeft className="h-4 w-4 text-muted-foreground" />
							</button>
							<span className="px-2 text-sm text-muted-foreground">
								Page {currentPage} of {totalPages}
							</span>
							<button
								type="button"
								onClick={() => setCurrentPage((value) => Math.min(totalPages, value + 1))}
								disabled={currentPage === totalPages || activitiesQuery.isFetching}
								className="rounded-lg border border-border bg-white p-1.5 transition-colors hover:bg-secondary/50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<ChevronRight className="h-4 w-4 text-muted-foreground" />
							</button>
						</div>
					</div>
				)}
			</div>
		</ContentSection>
	);
}
